import React,{useState,useEffect} from 'react'
import {motion} from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import {useAuth} from './AuthProvider'
import AuthModal from './AuthModal'
import ShareUid from './ShareUid'

const {FiKey,FiSave,FiCheck,FiLoader,FiUser,FiEye,FiEyeOff,FiAlertCircle,FiShield, FiShare2}=FiIcons

const Settings=()=> {
  const {user}=useAuth()
  const [showAuthModal,setShowAuthModal]=useState(false)
  const [apiKey,setApiKey]=useState('')
  const [showApiKey,setShowApiKey]=useState(false)
  const [loading,setLoading]=useState(false)
  const [saveSuccess,setSaveSuccess]=useState(false)
  const [error,setError]=useState('')
  const [loadingSettings,setLoadingSettings]=useState(true)

  useEffect(()=> {
    if (user) {
      loadUserSettings()
    } else {
      setLoadingSettings(false)
    }
  },[user])

  const loadUserSettings=async ()=> {
    try {
      setLoadingSettings(true)
      // Load settings from localStorage for No Code Backend
      const savedApiKey=localStorage.getItem('openai-api-key')
      if (savedApiKey) {
        setApiKey(savedApiKey)
      }
    } catch (err) {
      console.error('Error loading settings:',err)
      setError('Failed to load settings')
    } finally {
      setLoadingSettings(false)
    }
  }

  const saveApiKey=async ()=> {
    if (!user) {
      setShowAuthModal(true)
      return
    }
    if (!apiKey.trim()) {
      setError('Please enter your OpenAI API key')
      return
    }
    if (!apiKey.startsWith('sk-')) {
      setError('Invalid API key format. OpenAI API keys start with "sk-"')
      return
    }
    setLoading(true)
    setError('')
    try {
      // Save to localStorage for No Code Backend
      localStorage.setItem('openai-api-key',apiKey.trim())
      setSaveSuccess(true)
      setTimeout(()=> setSaveSuccess(false),3000)
    } catch (err) {
      console.error('Error saving API key:',err)
      setError('Failed to save API key. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const testApiKey=async ()=> {
    if (!apiKey.trim()) {
      setError('Please enter your API key first')
      return
    }
    setLoading(true)
    setError('')
    try {
      const response=await fetch('/api/test-openai',{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({apiKey: apiKey.trim()}),
      })

      if (!response.ok) {
        let errorMessage=`HTTP error! status: ${response.status}`
        try {
          const errorData=await response.json()
          errorMessage=errorData.error || errorMessage
        } catch (jsonError) {
          // If we can't parse JSON,check if it's HTML
          const textResponse=await response.text()
          if (textResponse.includes('&lt;!DOCTYPE')) {
            errorMessage='API endpoint not found. Please check your deployment configuration.'
          } else {
            errorMessage=`Server error: ${textResponse.substring(0,100)}...`
          }
        }
        throw new Error(errorMessage)
      }

      const data=await response.json()
      setSaveSuccess(true)
      setTimeout(()=> setSaveSuccess(false),3000)
    } catch (err) {
      console.error('Error testing API key:',err)
      setError(err.message || 'Failed to test API key')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <SafeIcon icon={FiKey} className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">API Configuration</h2>
            <p className="text-sm text-slate-600">Manage your OpenAI API key securely</p>
          </div>
        </div>

        {!user ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <SafeIcon icon={FiUser} className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-900 mb-2">Sign In Required</h3>
            <p className="text-red-700 mb-4">
              Please sign in to securely store your OpenAI API key
            </p>
            <button
              onClick={()=> setShowAuthModal(true)}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Sign In / Sign Up
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {loadingSettings ? (
              <div className="flex items-center justify-center py-8">
                <SafeIcon icon={FiLoader} className="w-6 h-6 animate-spin text-red-500" />
                <span className="ml-2 text-slate-600">Loading settings...</span>
              </div>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      Signed in as: {user.email}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    OpenAI API Key
                  </label>
                  <div className="relative">
                    <SafeIcon
                      icon={FiKey}
                      className="absolute left-3 top-3 w-5 h-5 text-slate-400"
                    />
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e)=> setApiKey(e.target.value)}
                      className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="sk-your-openai-api-key-here"
                    />
                    <button
                      onClick={()=> setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-3 p-1 hover:bg-slate-100 rounded transition-colors"
                    >
                      <SafeIcon
                        icon={showApiKey ? FiEyeOff : FiEye}
                        className="w-5 h-5 text-slate-400"
                      />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Your API key is stored locally in your browser
                  </p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-600" />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex space-x-4">
                  <motion.button
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                    onClick={saveApiKey}
                    disabled={loading}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                      saveSuccess
                        ? 'bg-green-500 text-white'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <SafeIcon
                      icon={loading ? FiLoader : saveSuccess ? FiCheck : FiSave}
                      className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                    />
                    <span>
                      {loading ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save API Key'}
                    </span>
                  </motion.button>
                  <motion.button
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                    onClick={testApiKey}
                    disabled={loading || !apiKey.trim()}
                    className="px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Test Key
                  </motion.button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <SafeIcon icon={FiShare2} className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Share Session</h2>
            <p className="text-sm text-slate-600">Share a link with your user ID for debugging or collaboration.</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          This unique link includes your user ID. Sharing it allows others (like a support team) to view your saved prompts and settings temporarily. It's useful for troubleshooting.
        </p>
        <ShareUid />
      </div>

      <div className="bg-slate-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-slate-800 mb-4">How to Get Your OpenAI API Key</h3>
        <ol className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start space-x-2">
            <span className="bg-red-100 text-red-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
              1
            </span>
            <span>
              Visit{' '}
              <a
                href="https://platform.openai.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 hover:underline"
              >
                OpenAI Platform
              </a>
            </span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="bg-red-100 text-red-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
              2
            </span>
            <span>Sign up or log in to your account</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="bg-red-100 text-red-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
              3
            </span>
            <span>Navigate to "API Keys" in your dashboard</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="bg-red-100 text-red-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
              4
            </span>
            <span>Click "Create new secret key"</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="bg-red-100 text-red-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
              5
            </span>
            <span>Copy the key (starts with "sk-") and paste it above</span>
          </li>
        </ol>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={()=> setShowAuthModal(false)} />
    </div>
  )
}

export default Settings